import React from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

const ShiftCalendar = ({ 
  shiftList = [], 
  employeeList = [], 
  handleEdit, 
  viewMode = "weekly", 
  currentDate = dayjs(),
  onCardClick,
  getShiftColor
}) => {
  const { t } = useTranslation();

  // 1. Generate dates for columns
  const getDatesArray = () => {
    if (viewMode === "daily") {
      return [currentDate];
    } else if (viewMode === "weekly") {
      const start = currentDate.startOf("week");
      const days = [];
      for (let i = 0; i < 7; i++) {
        days.push(start.add(i, "day"));
      }
      return days;
    } else {
      // Monthly view - Only return days belonging to this month
      const start = currentDate.startOf("month");
      const end = currentDate.endOf("month");
      const days = [];
      let day = start;
      while (day.isBefore(end) || day.isSame(end, "day")) {
        days.push(day);
        day = day.add(1, "day");
      }
      return days;
    }
  };

  const datesArray = getDatesArray();
  const firstDateOfView = datesArray[0];
  const lastDateOfView = datesArray[datesArray.length - 1];

  // 2. Helper to get all shifts for an employee, filtered by the view range
  const getShiftsForEmployee = (employeeId) => {
    let employeeShifts = shiftList.filter((shift) => {
      if (employeeId === 'unassigned') return !shift.employeeId;
      return shift.employeeId?._id === employeeId || shift.employeeId === employeeId;
    });

    return employeeShifts
      .map((shift) => {
        let shiftStart = dayjs(shift.startDateTime || shift.date);
        let shiftEnd = dayjs(shift.endDateTime || shift.date);

        if (shiftEnd.isBefore(shiftStart) || shiftEnd.isSame(shiftStart, 'day')) {
          shiftEnd = shiftStart;
        }

        // FILTER 1: Hide shifts that ended BEFORE the current view
        if (shiftEnd.isBefore(firstDateOfView, 'day')) {
          return null;
        }

        // FILTER 2: Hide shifts that start AFTER the current view
        if (shiftStart.isAfter(lastDateOfView, 'day')) {
          return null;
        }

        return {
          ...shift,
          _start: shiftStart,
          _end: shiftEnd
        };
      })
      .filter(shift => shift !== null);
  };

  // 3. Check if a shift is active on a specific date
  const isShiftActiveOnDate = (shift, date) => {
    const dayStart = date.startOf('day');
    const shiftStart = shift._start.startOf('day');
    const shiftEnd = shift._end.startOf('day');
    
    return (dayStart.isSame(shiftStart) || dayStart.isAfter(shiftStart)) && 
           (dayStart.isSame(shiftEnd) || dayStart.isBefore(shiftEnd));
  };

  const handleCardClick = (e, shift = null) => {
    e.stopPropagation();
    if (onCardClick) {
      onCardClick(shift);
    } else if (shift) {
      handleEdit(shift);
    }
  };

  const formatTime = (time) => {
    if (!time) return "";
    if (typeof time === 'string' && time.includes(':')) {
      return time.substring(0, 5);
    }
    return time;
  };

  const getTextColor = (bgColor) => 'text-gray-800';
  const isToday = (date) => date.isSame(dayjs(), "day");

  // ═══ UPDATED WIDTH & HEIGHT CONSTANTS ═══
  const CELL_WIDTH = 140; // px (Increased width)
  const CELL_HEIGHT = 80; // px (Increased height for 2 lines)
  const EMPLOYEE_COL_WIDTH = 220; // px (Slightly wider for longer names)
  
  const totalGridWidth = datesArray.length * CELL_WIDTH;
  const fullGridTemplate = `repeat(${datesArray.length}, ${CELL_WIDTH}px)`;

  return (
    <div className="p-4 w-full">
      <div className="border rounded-lg overflow-x-auto bg-white pb-2 shadow-sm">
        {/* The minimum width forces the scrollbar to appear when needed */}
        <div className="min-w-[800px]" style={{ minWidth: `${totalGridWidth + EMPLOYEE_COL_WIDTH}px` }}>
          
          {/* ================= HEADER ROW ================= */}
          <div className="flex border-b border-[#E3E3E3] bg-[#E9EDF9] sticky top-0 z-20">
            <div 
              className="flex-shrink-0 border-r border-[#E3E3E3] font-semibold text-xs text-gray-700 uppercase flex items-center justify-center"
              style={{ width: `${EMPLOYEE_COL_WIDTH}px`, height: `${CELL_HEIGHT}px` }}
            >
              {t("EMPLOYEE")}
            </div>

            <div className="grid flex-grow" style={{ gridTemplateColumns: fullGridTemplate }}>
              {datesArray.map((day, index) => {
                const isTodayDay = isToday(day);
                return (
                  <div 
                    key={index} 
                    className={`border-r border-[#E3E3E3] last:border-r-0 flex flex-col justify-center items-center ${isTodayDay ? 'bg-blue-50' : ''}`}
                    style={{ height: `${CELL_HEIGHT}px` }}
                  >
                    <div className="text-[10px] font-semibold text-gray-500 uppercase leading-tight">
                      {day.format("ddd")}
                    </div>
                    <div className={`text-xs font-bold leading-tight ${isTodayDay ? 'text-blue-600' : 'text-gray-700'}`}>
                      {day.format("MMM D")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ================= BODY ROWS ================= */}
          <div className="divide-y divide-[#E3E3E3]">
            
            {/* Rows for Real Employees only */}
            {employeeList.map((employee) => {
              const employeeShifts = getShiftsForEmployee(employee._id);

              return (
                <div key={employee._id} className="flex">
                  
                  {/* Employee Name Column */}
                  <div 
                    className="flex-shrink-0 border-r border-[#E3E3E3] bg-white flex flex-col justify-center px-3"
                    style={{ width: `${EMPLOYEE_COL_WIDTH}px`, height: `${CELL_HEIGHT}px` }}
                  >
                    <div className="text-sm font-bold text-gray-800 truncate leading-tight">
                      {employee.name}
                    </div>
                    <div className="text-xs text-gray-400 font-medium leading-tight">
                      {employee.employeeId}
                    </div>
                  </div>

                  {/* Calendar Columns for this Employee */}
                  <div 
                    className="relative grid flex-grow bg-white"
                    style={{ gridTemplateColumns: fullGridTemplate }}
                  >
                    {/* Render grid cells and shifts */}
                    {datesArray.map((day, idx) => (
                      <div 
                        key={idx} 
                        className={`border-r border-[#E3E3E3] last:border-r-0 p-1 relative group`}
                        style={{ height: `${CELL_HEIGHT}px` }}
                      >
                        {/* ═══ RENDER INDIVIDUAL CARDS PER DATE ═══ */}
                        {employeeShifts.map((shift, shiftIdx) => {
                          if (!isShiftActiveOnDate(shift, day)) return null;

                          const shiftName = shift?.name || "Shift";
                          const bgColor = getShiftColor ? getShiftColor(shiftName) : '#F3F4F6';
                          const textColor = getTextColor(bgColor);

                          return (
                            <div
                              key={shiftIdx}
                              className="w-full h-full cursor-pointer transition-all hover:shadow-md rounded border border-transparent px-3 py-1.5 flex flex-col justify-center"
                              style={{ backgroundColor: bgColor, color: textColor }}
                              onClick={(e) => handleCardClick(e, shift)}
                            >
                              {/* Line 1: Shift Name (Bold, bigger) */}
                              <div className="font-semibold text-sm truncate leading-tight">
                                {shift.name || shift.shiftName || "Shift"}
                              </div>
                              
                              {/* Line 2: Time (Smaller, lighter opacity) */}
                              <div className="opacity-90 text-xs truncate leading-tight mt-0.5">
                                {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                              </div>
                            </div>
                          );
                        })}

                        {/* ═══ ADD BUTTON IF EMPTY ═══ */}
                        {isEmpty(employeeShifts.filter(s => isShiftActiveOnDate(s, day))) && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className="pointer-events-auto w-6 h-6 rounded-full bg-blue-500 bg-opacity-10 flex items-center justify-center hover:bg-opacity-20 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                // ═══ PASS EMPLOYEE, DATE, AND DEPARTMENT ID ═══
                                if (onCardClick) {
                                  onCardClick(null, employee, day, employee.departmentId);
                                }
                              }}
                            >
                              <span className="text-blue-500 text-sm font-bold">+</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Fallback if no employees exist */}
            {isEmpty(employeeList) && isEmpty(shiftList) && (
              <div className="p-10 text-center text-gray-400 w-full">
                {t("NO_RECORDS_FOUND")}
              </div>
            )}
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftCalendar;